import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Icon from 'components/Utils/NewIcon';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import moment from 'moment-timezone';
import DefaultTextField from 'components/Utils/DefaultTextField';
import './style.scss';
import AppointmentUtils from 'services/AppointmentUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import AddressFields from 'components/Patient/EditAddress/AddressFields';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import PatientBadge from 'components/Patient/PatientBadge';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import PatientSearchField from 'components/Patient/PatientSearchField';
import _ from 'lodash';
import StaffTimePicker from 'components/Utils/StaffTimePicker';
import ClinicSelector from 'components/Utils/ClinicSelector';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { isResponseError } from 'careand-redux/api/SerializedReponseError';
import CalendarUtils from 'services/CalendarUtils';
import CalendarView from 'components/HealthRecords/Appointments/NewAppointment/CalendarView';
import SchedulePhysician from 'components/HealthRecords/Appointments/NewAppointment/SchedulePhysician';
import PerformanceUtils from 'services/PerformanceUtils';
import ResponsiveColumns from 'components/ResponsiveColumns';
import AppointmentStatusHistory from 'components/HealthRecords/Appointments/AppointmentStatusHistory';
import BookingConcernPicker from 'components/HealthRecords/Appointments/BookingConcernPicker';

let BUTTON_STYLE = {
  marginLeft: '2.5%',
  marginRight: '2.5%',
  width: '45%',
  marginTop: '20px',
};

const EditAppointment = ({
  appointment,
  showPatient,
  defaultShowCalendar,
  hideCalendar,
  onClose,
}) => {
  const dispatch = useAppDispatch();

  const options = useAppSelector((state) => state.appointments.options);

  const initialValue = useMemo(
    () => addTypeAndConcern(addTimeToAppointment(appointment), options),
    [appointment, options],
  );

  const [value, setValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState();

  const patient = useAppSelector((state) => state.patients.byId[value.patient_id]);
  const allPhysicians = useAppSelector((state) => state.physicians.byId);
  const admins = useAppSelector((state) => state.admins.byId);
  const shifts = useAppSelector((state) => state.shifts.shifts);

  const filteredShifts = useMemo(() => {
    return CalendarUtils.filterShifts(shifts, moment.unix(value.date), value.clinic_id);
  }, [shifts, value.date, value.clinic_id]);

  const concern = useMemo(() => {
    return _.flatten(options.map(({ concerns }) => concerns)).find(
      (concern) => concern?.id === value.concern_id,
    );
  }, [options, value.concern_id]);

  let availablePhysicians = useMemo(() => {
    return AppointmentUtils.getAvailablePhysicians(
      allPhysicians,
      filteredShifts,
      value.clinic_id,
      value.patient_id,
      concern,
      value.physician_id,
    );
  }, [
    allPhysicians,
    filteredShifts,
    value.clinic_id,
    value.patient_id,
    value.physician_id,
    concern,
  ]);

  useEffect(() => {
    let id = availablePhysicians.find(
      (physician) => !value.physician_id || value.physician_id === physician.id,
    );

    if (!id) setValue({ ...value, physician_id: undefined });
  }, [value, availablePhysicians]);

  function addTypeAndConcern(appointment, options) {
    appointment = PerformanceUtils.clone(appointment);

    let option = options.find(
      (option) => !!option.concerns?.find(({ id }) => appointment.concern_id === id),
    );

    if (!!option) appointment.option_id = option.id;

    return appointment;
  }

  function addTimeToAppointment(appointment) {
    appointment = PerformanceUtils.clone(appointment);
    appointment.time = appointment.date;
    appointment.time_end = appointment.date_end;
    return appointment;
  }

  async function onConfirmSlot() {
    setConfirmationDialog();
    await saveAppointment(undefined, true);
  }

  function resetChanges() {
    setValue(initialValue);
  }

  // Handles the single-field BookingConcernPicker. It reports the picked
  // concern plus the resolved parent option id and (when delivery options
  // exist) the chosen delivery option. We mirror the old type/concern/delivery
  // selects here: apply the delivery option fields, keep delivery_option_index
  // in sync, and recompute date_end from the concern duration.
  function handleConcernSelect(concernId, selection) {
    let newValue = { ...value, concern_id: concernId, delivery_option_index: undefined };

    if (selection?.optionId) newValue.option_id = selection.optionId;

    let pickedConcern = _.flatten(options.map(({ concerns }) => concerns || [])).find(
      (c) => c?.id === concernId,
    );

    if (selection?.deliveryOption) {
      let matchIndex = (pickedConcern?.delivery_options || []).findIndex(
        (opt) =>
          opt.type === selection.deliveryOption.type &&
          opt.telemedicine_method === selection.deliveryOption.telemedicine_method,
      );
      newValue = {
        ...newValue,
        ...selection.deliveryOption,
        delivery_option_index: matchIndex >= 0 ? matchIndex : 0,
      };
    }

    if (pickedConcern?.minutes) {
      let date_end = moment.unix(newValue.date).add(pickedConcern.minutes, 'minutes').unix();
      newValue = addTimeToAppointment({ ...newValue, date_end });
    }

    setValue(newValue);
  }

  function getStatusInputs() {
    let result = [];
    let statuses = AppointmentUtils.getAppointmentStatus();

    for (let key in statuses) result.push(statuses[key]);
    return result;
  }

  function handleDateChange(event, date) {
    setValue((prevState) => {
      return {
        ...prevState,
        date,
      };
    });
  }

  function handleTimeChange(event, time) {
    setValue((prevState) => {
      let time_end = prevState.time_end;
      if (concern) {
        time_end = moment.unix(time).add(concern.minutes, 'minutes').unix();
      }
      return {
        ...prevState,
        time,
        time_end,
      };
    });
  }

  function handleTimeEndChange(event, time_end) {
    setValue((prevState) => {
      let timeEnd = time_end;
      if (timeEnd < prevState.time) timeEnd += 24 * 60 * 60;
      return {
        ...prevState,
        time_end: timeEnd,
      };
    });
  }

  function onAddressSearchResult(address) {
    setValue((prevState) => {
      return {
        ...prevState,
        address,
      };
    });
  }

  function onAddressChange(event) {
    setValue({
      ...value,
      address: {
        ...value.address,
        [event.target.name]: event.target.value,
      },
    });
  }

  async function saveAppointment(event, confirmSlot = false) {
    setSaving(true);

    let appointment = PerformanceUtils.clone(value);

    let { date, time, time_end, date_end } = appointment;

    if (date && time) {
      date = moment.unix(date);
      time = moment.unix(time);
      date.hour(time.hour());
      date.minute(time.minute());
      date = date.unix();
      appointment.date = date;
    }

    if (date && time_end) {
      date_end = moment.unix(date);
      time_end = moment.unix(time_end);
      date_end.hour(time_end.hour());
      date_end.minute(time_end.minute());
      date_end = date_end.unix();
      appointment.date_end = date_end;
    }

    delete appointment.time;
    delete appointment.time_end;
    delete appointment.option_id;

    // TODO needs both, body and status
    const response = await dispatch(
      api.Appointments.updateAppointment({ confirmSlot, appointment }),
      {
        errorTitle: 'Appointment Update',
        // successTitle is load-bearing: handleSuccessIfNeeded only renders the
        // toast when a title is present (successText alone is silently dropped).
        successTitle: 'Appointment Update',
        successText: 'Appointment updated successfully!',
        meta: true,
      },
    );
    setSaving(false);

    if (
      AppointmentUtils.responseNeedsConfirmation(response.status) &&
      isResponseError(response.error)
    ) {
      setConfirmationDialog(response.error?.response.jsonBodyMessage);
    } else if (response.isSuccess && response.result) {
      let editedAppointment = addTypeAndConcern(addTimeToAppointment(response.result), options);
      setValue(editedAppointment);
      close(response.result);
    }
  }

  // Contract: callers rely on the argument to distinguish save from cancel.
  // Pass the updated appointment on a successful save; pass nothing on
  // reset/cancel. AppointmentDetails.handleEditClose keys off this to decide
  // whether to stay in the editor or close the surface — keep it intact.
  function close(appointment) {
    if (onClose) onClose(appointment);
  }

  const showBlockedPatients = value.type === AppointmentUtils.getAppointmentTypes().blocked;

  const statusHistory = useMemo(() => {
    return AppointmentUtils.getStatusHistoryEntries(value, allPhysicians, admins, patient);
  }, [value, allPhysicians, admins, patient]);

  const hasPatient = !!value.patient_id;
  const hasPhysician = !!value.physician_id;
  const hasPatientAndPhysician = hasPatient && hasPhysician;
  const isBlocked = value.type === AppointmentUtils.getAppointmentTypes().blocked;

  const isDirty = useMemo(() => !_.isEqual(value, initialValue), [value, initialValue]);

  const buttonDisabled =
    (isBlocked ? !hasPhysician : !concern || !hasPatientAndPhysician) || !isDirty;

  const detailsContent = (
    <div className="appointment-view scroll-list">
      {!!confirmationDialog && (
        <ConfirmationDialog
          title="Confirmation Needed"
          message={confirmationDialog}
          open={!!confirmationDialog}
          onConfirm={() => onConfirmSlot()}
          onCancel={() => setConfirmationDialog()}
          loadingState={false}
        />
      )}
      <div className="top-icons">
        <AppointmentStatusHistory entries={statusHistory} width="20px" />
      </div>
      <div className="top-bar">
        <TopBar allowClose={false} onClose={() => close()} />
      </div>
      <div className="data-content">
        {value.type === AppointmentUtils.getAppointmentTypes().blocked && (
          <DefaultTextField
            label="Appointment Type"
            value={AppointmentUtils.getAppointmentTypes().blocked}
            disabled={true}
          />
        )}
        {value.type !== AppointmentUtils.getAppointmentTypes().blocked && !!value.patient_id && (
          <BookingConcernPicker
            patientId={value.patient_id}
            value={value.concern_id}
            deliveryValue={value.type}
            deliveryTelemedicineMethod={value.telemedicine_method}
            withDeliveryOptions
            label="Appointment"
            width="100%"
            onChange={handleConcernSelect}
          />
        )}
        <div className="row full-width">
          <DefaultDatePicker
            name="date"
            label="Day"
            value={value.date}
            onChange={handleDateChange}
            style={{
              width: 'calc(50% - 10px)',
              minWidth: 'auto',
              display: 'inline-block',
              marginRight: '20px',
            }}
          />
          <DefaultSelectField
            label="Status"
            style={{ width: 'calc(50% - 10px)' }}
            value={value.status}
            onChange={(event) => {
              setValue({
                ...value,
                status: event.target.value,
              });
            }}
            inputs={getStatusInputs()}
          />
          <div className="row full-width">
            <StaffTimePicker
              name="time"
              label="Time"
              style={{
                width: 'calc(50% - 10px)',
                display: 'inline-block',
                marginRight: '20px',
              }}
              onChange={handleTimeChange}
              value={value.time}
            />
            {!!value.time_end && (
              <StaffTimePicker
                name="time_end"
                label="Time End"
                style={{ width: 'calc(50% - 10px)', display: 'inline-block' }}
                onChange={handleTimeEndChange}
                value={value.time_end}
              />
            )}
          </div>
        </div>

        <span className="small-title">Notes</span>
        <TextareaAutosize
          name="notes"
          value={value.notes || ''}
          onChange={(event) => setValue({ ...value, notes: event.target.value })}
        />

        <div className="row full-width">
          {value.type === AppointmentUtils.getAppointmentTypes().blocked ? (
            <DefaultTextField
              className="complaints"
              name="complaints"
              label="Reason"
              style={{ width: '100%', marginRight: '20px' }}
              value={value.complaints}
              onChange={(event) => {
                setValue({ ...value, complaints: event.target.value });
              }}
            />
          ) : (
            <>
              <span className="small-title">Complaints</span>
              <pre>{value.complaints}</pre>
            </>
          )}
        </div>

        {showPatient && !!patient && (
          <>
            <span className="wide-label">Patient</span>
            <PatientBadge patient={patient} />
          </>
        )}
        <ClinicSelector
          style={{ width: '100%', marginBottom: '10px' }}
          value={value.clinic_id}
          hideLabel={false}
          setValue={(id) => {
            setValue({
              ...value,
              clinic_id: id,
              physician_id: undefined,
            });
          }}
        />
        <span className="wide-label">Practitioner</span>
        <SchedulePhysician
          appointment={value}
          selectedId={value.physician_id}
          onClick={(physician_id) =>
            setValue({
              ...value,
              status: AppointmentUtils.getAppointmentStatus().scheduled,
              physician_id,
            })
          }
          physicians={availablePhysicians}
        />
        {value.type === 'Home' ? (
          <div className="row">
            <span className="small-title">Address</span>
            <AddressFields
              showNotes={true}
              address={value.address}
              onChange={onAddressChange}
              onSearchResult={onAddressSearchResult}
              style={{ width: '100%' }}
            />
          </div>
        ) : null}
        <div className="row" style={{ display: 'block', marginBottom: '10px' }}>
          <DefaultButton
            name="Reset Changes"
            onClick={resetChanges}
            style={BUTTON_STYLE}
            className="red"
          />
          <DefaultButton
            name="Save"
            onClick={saveAppointment}
            style={BUTTON_STYLE}
            disabled={buttonDisabled}
            loading={saving}
            className="blue"
          />
        </div>
      </div>
    </div>
  );

  const calendarColumn = hideCalendar
    ? undefined
    : {
        name: 'Calendar',
        element: (
          <CalendarView
            date={value.date}
            isCalendarVisible={true}
            physicians={availablePhysicians}
            selectPhysician={(physician_id) => {
              setValue((prevState) => {
                return {
                  ...prevState,
                  physician_id,
                };
              });
            }}
            setTimeAndDate={(date) => {
              handleTimeChange(undefined, date.unix());
              handleDateChange(undefined, date.unix());
            }}
            clinic_id={value.clinic_id}
          />
        ),
      };

  const blockedPatientsColumn = showBlockedPatients
    ? {
        name: 'Blocked Patients',
        element: <BlockedPatients value={value} setValue={setValue} showBlockedPatients={true} />,
      }
    : undefined;

  return (
    <div className={`details-panel appointment-edit${hideCalendar ? '' : ' show-calendar'}`}>
      <ResponsiveColumns
        defaultTab={!hideCalendar && defaultShowCalendar ? (showBlockedPatients ? 2 : 1) : 0}
        columns={[
          {
            name: 'Details',
            element: detailsContent,
          },
          blockedPatientsColumn,
          calendarColumn,
        ]}
      />
    </div>
  );
};

const BlockedPatients = ({ value, setValue, showBlockedPatients }) => {
  let patient_ids = value.patient_ids || [];

  function addPatient(patient) {
    if (patient) {
      let ids = [...patient_ids];
      ids.push(patient.id);
      ids = _.uniq(ids);
      setValue({ ...value, patient_ids: ids });
    }
  }

  function removePatient(toRemove) {
    setValue({
      ...value,
      patient_ids: patient_ids.filter((id) => id !== toRemove),
    });
  }

  return (
    showBlockedPatients && (
      <div className="blocked-appointment-patients" style={{ width: '370px' }}>
        <PatientSearchField onSelect={addPatient} title="Patients" key={patient_ids.length} />
        {patient_ids.map((id) => {
          return (
            <div style={{ display: 'flex', flexDirection: 'row' }} key={id}>
              <div style={{ flexGrow: 1 }}>
                <PatientBadge id={id} goToProfile={true} key={id} />
              </div>
              <Icon src="trash" width="30px" onClick={() => removePatient(id)} />
            </div>
          );
        })}
      </div>
    )
  );
};

const TopBar = ({ allowClose, onClose }) =>
  allowClose && (
    <div>
      <span className="small-title">Appointment Details</span>
      {onClose !== undefined && (
        <div className="close">
          <Icon src="xmark" onClick={onClose} />
        </div>
      )}
    </div>
  );

EditAppointment.propTypes = {
  appointment: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  defaultShowCalendar: PropTypes.bool,
  hideCalendar: PropTypes.bool,
  showPatient: PropTypes.bool,
};

EditAppointment.defaultProps = {
  showPatient: false,
  hideCalendar: false,
};

export default EditAppointment;
